import React, { useState, useEffect, useRef } from 'react';
import { 
  MessageSquare, 
  X, 
  Send, 
  Sparkles, 
  HelpCircle, 
  ExternalLink, 
  Mail, 
  ArrowRight, 
  CheckCircle2, 
  Bot, 
  User, 
  Loader2, 
  RotateCcw, 
  Key, 
  Calendar, 
  DollarSign, 
  FileText, 
  ShieldCheck,
  Minimize2,
  Maximize2,
  Shield,
  Lock
} from 'lucide-react';
import { FormattedMarkdown } from './FormattedMarkdown';
import { 
  getByokRequestHeaders, 
  loadByokVault, 
  saveByokVault, 
  AiProviderId, 
  AI_PROVIDERS_CONFIG 
} from '../utils/byokService';

interface AIConciergeWidgetProps {
  onNavigate: (path: string) => void;
  showToast: (msg: string, type?: 'success' | 'info') => void;
  isOpen?: boolean;
  onToggleOpen?: () => void;
  onOpenWhatIsAzuraOS?: () => void;
  onOpenContact?: () => void;
  onOpenFAQ?: () => void;
  onOpenSecurity?: () => void;
}

interface Message {
  id: string;
  sender: 'user' | 'assistant';
  text: string;
  timestamp: string;
  quickAction?: {
    label: string;
    action: () => void;
  };
}

interface FAQItem {
  question: string;
  keywords?: string[];
  answer: string;
  route?: string;
  link?: string;
  label?: string;
  onTrigger?: () => void;
}

const INSTANT_FAQS: FAQItem[] = [
  {
    question: 'How do I use AI Reconciler?',
    keywords: ['reconcile', 'ai reconciler', 'bank statement', 'reconciliation', 'reconciler', 'how to reconcile', 'reconciliation hub', 'reconcile statement', 'bank reconciliation'],
    answer: '### ⚡ AI Reconciliation Hub & Ledger Matcher\n\nThe **AI Reconciler** automatically categorizes bank statements, matches transactions to your revenue streams, and extracts line items from receipts in seconds:\n\n#### 📍 Where to Find & Open It:\n1. Navigate to **/ai-assistant** from the bottom navigation or Menu.\n2. Tap the **"AI Reconciler"** tab at the top.\n3. Alternatively, click **"AI Reconcile Hub"** directly from your Dashboard or Operational HQ.\n\n#### 🚀 4 Flexible Ways to Ingest Bank Activity:\n* **1. Bank PDF Statement Upload:** Drag & drop your PDF statement directly from online banking (FNB, Chase, Capitec, Barclays, etc.). AzuraOS automatically decompresses text streams and cleans merchant entities.\n* **2. Bank CSV / Excel Export (0.05s):** Download your statement as CSV from your online banking and drop it in for instant, zero-latency parsing.\n* **3. Paste Banking Lines:** Highlight transaction rows with your mouse on your bank\'s website, copy (`Ctrl+C`), click **"Paste Banking Lines"**, and paste.\n* **4. Direct Entry Grid:** Click **"+ Add Manually"** to type in entries row-by-row with live totals, currency conversions, and automated stream tagging.\n\n#### 🧾 Receipts & Invoices:\n* Snap a photo/screenshot (`.png`, `.jpg`), press **Ctrl+V** anywhere to paste from clipboard, or click **"Fill Manually"** to create an editable expense draft.',
    route: '/ai-assistant?pillar=reconciler',
    label: 'Open AI Reconciler Hub'
  },
  {
    question: 'Can I upload PDF bank statements?',
    keywords: ['pdf upload', 'pdf bank statement', 'can i upload pdf', 'pdf supported', 'fnb pdf', 'chase pdf', 'bank pdf', 'statement pdf', 'how to upload pdf'],
    answer: '### 📄 Yes! Full PDF, CSV, Paste & Manual Bank Statement Support\n\nAzuraOS features **Full-Spectrum Multi-Modal Ingestion** for your bank statements:\n\n* **1. Direct PDF Statement Uploads (with Stream Decompression):** Drag and drop PDF statements directly from your bank (e.g. FNB, Chase, Capitec, Barclays, Standard Bank). AzuraOS automatically decodes internal text streams and uses Gemini AI to clean merchant descriptions and map transactions.\n* **2. Instant 0.05s Bank CSV / Excel Exports:** Download a CSV directly from online banking for zero-latency, 100% deterministic parsing.\n* **3. Copy & Paste Online Banking Rows:** If your bank restricts downloads or you prefer raw text, simply highlight transaction lines with your mouse, copy (`Ctrl+C`), and paste them into the **Paste Banking Lines** drawer.\n* **4. Direct Manual Grid Entry:** Add, edit, and categorize ledger items row-by-row with live running totals and automated stream allocation.\n* **5. Receipt & Invoice OCR:** Drop receipt photos or press `Ctrl+V` to paste screenshots for instant line item extraction.',
    route: '/ai-assistant?pillar=reconciler',
    label: 'View Ingestion Methods'
  },
  {
    question: 'How do I export and upload a Bank CSV?',
    keywords: ['how to csv', 'how to upload csv', 'csv guide', 'export csv', 'bank csv', 'chase csv', 'fnb csv', 'capitec csv', 'wise csv', 'download csv'],
    answer: '### 📥 30-Second Bank CSV Export & Upload Guide\n\nExporting a CSV directly from your bank is the fastest, cleanest way to reconcile:\n\n1. **Log In:** Open your online banking portal or mobile app (e.g. Chase, FNB, Capitec, Wise, Barclays, Wells Fargo, etc.).\n2. **Open Activity:** Go to your **Account Activity**, **Transactions**, or **Statements** tab.\n3. **Click Download / Export:** Choose **CSV** or **Excel (.csv)** format and save it to your device.\n4. **Drop into AzuraOS:** Go to **/ai-assistant** ➔ **AI Reconciler**, and drag your `.csv` file directly into the dropzone.\n\nAzuraOS immediately structures all credits and debits, classifies revenue streams, and highlights tax-deductible expenses!',
    route: '/ai-assistant?pillar=reconciler',
    label: 'Open AI Reconciler'
  },
  {
    question: 'How do I scan or paste receipts & invoices?',
    keywords: ['receipt', 'invoice scan', 'ocr', 'paste receipt', 'ctrl+v receipt', 'fill receipt manually', 'receipt upload', 'how to scan receipt', 'invoice capture'],
    answer: '### 📸 Receipt & Invoice Ingestion Guide\n\nAzuraOS makes logging expense receipts and supplier invoices effortless:\n\n* **Instant Clipboard Paste (Ctrl+V):** Take a screenshot or copy an image, then press <kbd>Ctrl+V</kbd> (or <kbd>Cmd+V</kbd>) anywhere in the Hub to automatically trigger AI line-item extraction.\n* **Camera / Screenshot Photo:** Drop a `.jpg` or `.png` into the Receipt Scanner. AzuraOS automatically optimizes and compresses the image by 80% to ensure lightning-fast processing.\n* **Fill Manually Mode:** Click the **"Fill Manually"** button to log an expense draft directly without needing any photo or file.',
    route: '/ai-assistant?pillar=reconciler',
    label: 'Open Receipt Scanner'
  },
  {
    question: 'What is the Pilot Cohort program?',
    keywords: ['pilot cohort', 'cohort', 'pilot', 'what is the pilot', 'what is the pilot cohort about', 'incubator cohort', 'bank pilot', 'sponsorship cohort', 'enterprise pilot', 'partnership access', 'esd program', 'cohort size', 'institutional cohort', 'who is the pilot for'],
    answer: '### 🏛️ AzuraOS Pilot Cohort Program (In-App Guide)\n\nGreat question! The **Pilot Cohort** is our turnkey 30-day financial governance and solvency initiative designed for **incubators, commercial banks, accelerators, and enterprise sponsors** deploying AzuraOS across batches of 10 to 100+ solo founders and SMEs.\n\n#### 🎯 What Problem Does It Solve?\nTraditional incubation and lending programs wait 3 to 6 months for retrospective accounting statements, often discovering founder insolvency only after it is too late. AzuraOS gives both **the cohort founders** and **the sponsoring institution** real-time operational solvency telemetry from Day 1.\n\n#### 🗓️ How the 4-Week Pilot Works:\n* **Week 1 (Onboarding):** Bulk workspace provisioning, local tax presets, and sandboxed currency setups.\n* **Week 2 (Runway & Margin Control):** Connecting live revenue streams to the Margin Radar to instantly spotlight cash burn leaks.\n* **Week 3 (Invoicing & Hygiene):** Proforma billing, payment reconciliation, and audit-ready records.\n* **Week 4 (Sponsor Impact Scorecard):** Solvency certification, 92%+ survival analytics, and grant/lender reporting.\n\n#### 📍 Where to Find & Use It in AzuraOS:\n1. **Pilot Playbook & ROI Simulator:** Tap the **Menu** (top right) ➔ **"What is AzuraOS?"** or **"Concept & Mindset"** to test the interactive cohort calculator.\n2. **Enterprise & Sponsorship Inquiries:** Tap the **Menu** ➔ under *ENTERPRISE & BANKS*, select **"Partnership Access"**.\n3. **Direct Founder Support Desk:** You can also email us directly at **support@azuraos.app**.',
    route: '/what-is-azuraos',
    label: 'Explore Pilot Cohort & Playbook'
  },
  {
    question: 'How do I create client agreements & proformas?',
    keywords: ['client agreement', 'proforma', 'term sheet', 'statement of work', 'sow', 'contract', 'milestone', 'agreement', 'how do i create client agreement', 'proforma invoice'],
    answer: '### 📜 Client Agreement & Milestone Proforma Hub\n\nYou can generate commercial client agreements, milestone proformas, and term sheets directly in your workspace:\n\n1. **Open AI Co-Founder:** Navigate to **/ai-assistant** from the bottom navigation or Menu.\n2. **Select Tab:** Tap the **"Client Agreements & Proforma"** tab at the top.\n3. **Configure Engagement:** Fill in your client\'s company name, authorized signatory, milestone breakdown (e.g. 50% deposit / 50% delivery), and revision turnaround SLA.\n4. **Export Authentically:** In **Sovereign BYOK Mode**, connect your personal Google AI Studio key to unlock official high-res PDF generation, active invoice conversion to your receivables ledger, and clipboard exports with authentic commercial branding.',
    route: '/ai-assistant',
    label: 'Open Client Agreement Hub'
  },
  {
    question: 'Are my bank statements and receipts safe? Does AzuraOS store them?',
    keywords: ['bank statement', 'bank statements', 'bank safe', 'bank privacy', 'is bank safe', 'upload bank statement', 'banking', 'store bank statement', 'firebase bank', 'receipt safe', 'receipt storage', 'ai reconciler safe', 'reconciler safe', 'bank account connection', 'plaid'],
    answer: '### 🛡️ Sovereign Banking & Reconciler Security Guarantee\n\n**100% Safe, Air-Gapped & Ephemeral.** AzuraOS is engineered so you never have to expose your bank credentials or store sensitive files on our servers:\n\n* **1. Zero Bank Account Linking:** AzuraOS is completely air-gapped. We never ask for online banking logins, passwords, or intrusive Plaid/OAuth permissions. AzuraOS cannot debit, transfer, or modify your banking in any way.\n* **2. Ephemeral In-Memory Processing (Zero File Storage):** Uploaded CSVs, PDF statements, and receipt images are ingested **strictly in volatile RAM** solely to parse line items. The moment extraction finishes, the original file is **immediately purged**. AzuraOS does NOT save raw statement files or receipt images to any server disk or cloud storage bucket.\n* **3. Personal PII & Account Digits Excluded:** The ingestion engine strictly isolates transaction lines (date, vendor, amount). Bank account numbers, sort codes, routing numbers, home addresses, and live account balances are stripped out and discarded.\n* **4. Where Data Lives:** Parsed transactions are stored locally on your device (`localStorage`). If you choose to sync your account, they are stored in your private Firebase Cloud Vault under your own authenticated User ID (`request.auth.uid == userId`) with strict row-level security.\n* **5. Zero AI Model Training:** Ingested financial data is **never used to train base AI models**, crawled, or sold. Inference runs under enterprise Google Cloud GenAI agreements prohibiting data retention for model training.',
    route: '/ai-assistant?pillar=reconciler',
    label: 'Open AI Reconciler Hub'
  },
  {
    question: 'Is this App safe to use?',
    keywords: ['is this app safe to use', 'is this app safe', 'safe to use', 'safe', 'is it safe', 'security', 'privacy', 'is my data safe', 'data protection', 'encryption'],
    answer: '### 🛡️ Sovereign Security & Privacy Architecture\n\n**Yes, 100%!** AzuraOS is engineered from the ground up for solopreneur data sovereignty:\n\n* **Zero AI Model Training:** Your financial figures, client bookings, and strategic inputs are **never** used to train base AI models.\n* **Sandboxed Isolation:** Powered by Google Cloud Firestore with strict row-level security rules ensuring only your authenticated session has access.\n* **Sovereign BYOK Mode:** Connect your own Google AI Studio key for direct browser-to-model execution with zero middleman logging.\n* **Google API Compliance:** Calendar sync strictly complies with Google API Services User Data Policy (Limited Use).\n* **100% Data Sovereignty:** Export full JSON/CSV ledgers or wipe your account permanently anytime with 1 click.',
    route: '/security',
    label: 'View Security Safeguards'
  },
  {
    question: 'Does AzuraOS have security certificates, banking licenses, or CPA credentials?',
    keywords: ['certificate', 'certificates', 'certification', 'license', 'licence', 'banking license', 'cpa', 'work permission', 'financial license', 'trust', 'regulatory', 'compliance', 'security certificate', 'money transmitter', 'open banking', 'legal certificate', 'regulative'],
    answer: '### 🛡️ Trust, Certifications & Regulatory Architecture\n\n**AzuraOS is non-custodial operational intelligence software** — meaning you don’t have to trust third-party servers blindly, and we face zero banking regulatory liability:\n\n#### 🏛️ 1. Tier-1 Cloud Certifications (Google Cloud)\nOur backend services run on Google Cloud Platform and Firebase Firestore, inheriting enterprise certifications:\n* **SOC 1, SOC 2 Type II, and SOC 3** independent compliance attestations\n* **ISO/IEC 27001, 27017, and 27018** information security management systems\n* **PCI-DSS Level 1** certified hosting infrastructure\n* Official member of the **Google Cloud Partner Advantage Network** & **Google for Startups Cloud Program (Start Tier)** verified for domain `azuraos.app`.\n\n#### 💳 2. Why We Do Not Need an Open Banking or Money Transmitter License\n* **Non-Custodial:** We never log into personal/business bank accounts or scrape online banking passwords (which is what requires Open Banking AISP/PSD2 licenses). You upload CSVs or paste transaction rows directly.\n* **Zero Custodial Risk:** We never hold, transmit, or escrow user funds (which avoids Money Transmitter / EMI licensing rules).\n* **Merchant of Record (Dodo Payments):** Checkouts and subscription billing are managed through Dodo Payments on certified PCI-DSS Level 1 infrastructure.\n\n#### 🔐 3. Client-Side Zero-Knowledge Cryptography (AES-256-GCM)\n* AzuraOS features a client-side **Web Crypto vault**. You can encrypt sensitive financial payloads and API keys directly in your browser with your private passphrase. Even platform operators cannot read raw encrypted data.\n\n#### ⚖️ 4. AI CFO & Legal Co-Founder Disclaimers\n* AzuraOS provides operational mathematical and strategic copilots (margin calculations, tax reserve formulas, contract clause reviews). It does not provide regulated CPA fiduciary accounting or Bar-licensed legal advice.\n\nTap below to test the live encryption vault and inspect the full compliance matrix anytime!',
    route: '/security',
    label: 'Open Trust, Certifications & Vault'
  },
  {
    question: 'How is AzuraOS different from other accounting software?',
    keywords: ['how is azuraos different', 'different from other accounting', 'quickbooks', 'xero', 'difference', 'why azuraos', 'vs quickbooks', 'vs other'],
    answer: '### ⚡ 1 Unified Operating System vs. 5 Disconnected Tools\n\nAzuraOS is **not traditional bookkeeping software, not another spreadsheet, and not an administrative chore**.\n\nIt is the **1-system operating layer for solo founders** — replacing the 10+ hours a week lost to disconnected tabs (Calendly, DocuSign, QuickBooks, Notion, and spreadsheets).\n\n### 1. Unified Execution Flow vs. Disjointed Tabs\n* **Traditional SaaS Stack:** Client books on Calendly, you manually draft a contract in DocuSign, copy details to QuickBooks, and guess your take-home pay in Excel.\n* **AzuraOS:** 1 continuous flow. When a client books, your legal agreement, milestone deposit invoice, and live margin defense update instantly.\n\n### 2. Live Margin Defense Radar vs. Lagged Accounting\n* **Traditional Accounting:** Tells you what happened 3 months ago in debit/credit jargon.\n* **AzuraOS:** Real-time net safe spendable owner cash after automatic tax reserve buffers and fixed monthly burn.\n\n### 3. Air-Gapped Statement Parser (0.05s)\n* **Traditional Tools:** Require risky third-party bank password scraping.\n* **AzuraOS:** Drop any bank PDF or CSV to parse and categorize deductions in seconds with zero credential sharing.\n\n### 4. Autonomous AI Co-Founder (100% BYOK)\n* **Traditional Tools:** Generic support bots or expensive fractional CFOs.\n* **AzuraOS:** Private BYOK Gemini intelligence that audits pricing, detects contract risk, and drafts debt collection notices.',
    route: '/dashboard',
    label: 'Explore Live Dashboard & J-Lens'
  },
  {
    question: 'What is this app used for?',
    keywords: ['what is this app', 'used for', 'what does this app do', 'what is azuraos', 'who is this for', 'what can i do here', 'overview'],
    answer: 'AzuraOS is the 1-system operating layer for solo founders. It eliminates weekly admin chaos by connecting client scheduling, binding agreements, 50% milestone invoicing, air-gapped bank reconciliation, and live margin intelligence in one place.',
    route: '/dashboard',
    label: 'Explore Dashboard'
  },
  {
    question: 'How do I hide my financial numbers in public?',
    keywords: ['hide numbers', 'privacy mode', 'mask numbers', 'hide financial', 'hide revenue', 'public mode', 'working in public'],
    answer: '### 👁️ Privacy Mode (Financial Masking)\n\nWorking in a coffee shop or public space? You can mask all sensitive revenue, expense, and client figures with asterisks (**••••**) with 1 tap:\n\n1. Tap the **Menu** (top right).\n2. Scroll to the **Privacy Mode** block.\n3. Tap **Masked / Visible** to toggle financial masking on or off immediately.',
    label: 'Check Menu Privacy Mode'
  },
  {
    question: 'How do I report a bug or invite a founder?',
    keywords: ['report bug', 'invite founder', 'feedback', 'bug', 'invite', 'share app', 'tell a friend', 'contact support'],
    answer: '### 🤝 Founder Feedback & Community Invites\n\nWe love founder feedback and community growth!\n\n1. **Report a Bug / Request a Feature:** Tap the **Menu** (top right) ➔ scroll to the footer actions ➔ tap **"Report Bug"** to open our feedback form or email support@azuraos.app directly.\n2. **Invite a Founder:** Tap the **Menu** ➔ tap **"Invite Founder"** to share your invite link with other solopreneurs.\n3. **Direct Email:** Need immediate guidance? Reach our founder team directly at **support@azuraos.app**.',
    label: 'Open Menu Actions'
  },
  {
    question: 'My subscription hasn’t updated / Payment status',
    keywords: ['subscription hasn', 'subscription not', 'payment status', 'upgrade not showing', 'subscription pending', 'billing update', 'paid but not updated'],
    answer: 'Subscription activations via Dodo Payments update immediately. If you completed checkout and your account is still refreshing:\n1. Head to Billing and click "Sync Subscription" or reload your page.\n2. Ensure checkout was completed with your account email.\n3. If it still shows pending, email support@azuraos.app with your receipt for instant activation.',
    route: '/billing',
    label: 'Open Billing & Sync'
  },
  {
    question: 'How do I connect BYOK Google AI key?',
    keywords: ['byok', 'gemini key', 'api key', 'google ai studio', 'ai key', 'bring your own key'],
    answer: 'You can connect your free Google AI Studio key in 60 seconds! Go to our Welcome Aboard hub or Settings, get a key from aistudio.google.com/app/apikey, and paste it. Google provides 1,500 free queries every day with zero middleman markup.',
    route: '/welcome',
    label: 'Open BYOK Setup Guide'
  },
  {
    question: 'How does the 7-day free trial work?',
    keywords: ['free trial', 'trial', '7-day', 'trial period', 'pricing', 'how much'],
    answer: 'Your 7-day free trial gives you full access to Sovereign Founder Pro with unlimited bookings, expenses, Google Calendar sync, J-Lens analytics, and AI tools. You can upgrade anytime or manage billing under Billing.',
    route: '/billing',
    label: 'View Billing & Plan Details'
  },
  {
    question: 'How do I sync Google Calendar?',
    keywords: ['google calendar', 'calendar sync', 'sync calendar', 'calendar', 'appointments'],
    answer: 'Head to the Calendar tab in AzuraOS. Click "Connect Google Calendar" to grant 2-way sync permissions. Any booked client appointments will automatically reflect in your schedule and margin forecasts.',
    route: '/calendar',
    label: 'Go to Calendar Manager'
  },
  {
    question: 'How is real profit margin calculated?',
    keywords: ['margin', 'profit margin', 'calculate margin', 'formula', 'gross margin', 'margin radar'],
    answer: 'Real Margin % = ((Total Confirmed Bookings - Total Expenses) / Total Confirmed Bookings) * 100. AzuraOS constantly monitors this in real-time on your Margin Radar (/dashboard) and alerts you if your operating expenses push your business below your safety margin threshold.',
    route: '/dashboard',
    label: 'Check Dashboard Margin'
  },
  {
    question: 'How do I generate client invoices?',
    keywords: ['invoice', 'invoices', 'pdf invoice', 'billing client', 'send invoice'],
    answer: 'Navigate to the Invoices tab. All your logged client bookings are automatically formatted into professional, tax-ready invoices with one-click PDF downloads and payment status tracking.',
    route: '/invoices',
    label: 'Open Invoicing Hub'
  },
  {
    question: 'How do I backup or export my data?',
    keywords: ['backup', 'export', 'csv', 'download data', 'data backup', 'excel'],
    answer: 'Your business data is 100% sovereign! You can export full JSON backups, CSV bookings schedules, expense reports, and tax packs anytime from the Import & Export Center on your Dashboard or Operational HQ.',
    route: '/dashboard',
    label: 'Open Export Center'
  }
];

export function AIConciergeWidget({ 
  onNavigate, 
  showToast, 
  isOpen: externalIsOpen, 
  onToggleOpen,
  onOpenWhatIsAzuraOS,
  onOpenContact,
  onOpenFAQ,
  onOpenSecurity
}: AIConciergeWidgetProps) {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isWidgetOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;

  const toggleWidget = () => {
    if (onToggleOpen) {
      onToggleOpen();
    } else {
      setInternalIsOpen(prev => !prev);
    }
  };

  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [viewportHeight, setViewportHeight] = useState<number | null>(null);
  const [keyboardOffset, setKeyboardOffset] = useState<number>(0);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Track Mobile Visual Viewport & Keyboard Appearance only when widget is open
  useEffect(() => {
    if (!isWidgetOpen || typeof window === 'undefined' || !window.visualViewport) {
      setKeyboardOffset(0);
      setViewportHeight(null);
      return;
    }

    let ticking = false;
    const handleViewportChange = () => {
      if (!ticking && window.visualViewport) {
        window.requestAnimationFrame(() => {
          const vv = window.visualViewport;
          if (vv) {
            const offset = window.innerHeight - vv.height - vv.offsetTop;
            const newOffset = offset > 40 ? Math.round(offset) : 0;
            setKeyboardOffset(prev => (Math.abs(prev - newOffset) > 5 ? newOffset : prev));
            setViewportHeight(prev => (prev !== Math.round(vv.height) ? Math.round(vv.height) : prev));
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.visualViewport.addEventListener('resize', handleViewportChange);
    handleViewportChange();

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportChange);
      }
    };
  }, [isWidgetOpen]);

  const [vault, setVault] = useState(() => loadByokVault());
  const [activeProvider, setActiveProvider] = useState<AiProviderId>(() => loadByokVault().activeProvider || 'gemini');

  useEffect(() => {
    const handleVaultUpdate = () => {
      const v = loadByokVault();
      setVault(v);
      setActiveProvider(v.activeProvider || 'gemini');
    };
    window.addEventListener('azuraos_byok_updated', handleVaultUpdate);
    return () => window.removeEventListener('azuraos_byok_updated', handleVaultUpdate);
  }, []);

  const handleProviderSelect = (pid: AiProviderId) => {
    const current = loadByokVault();
    const updated = { ...current, activeProvider: pid };
    saveByokVault(updated);
    setVault(updated);
    setActiveProvider(pid);
    showToast(`AI Concierge & Operational Engine switched to ${AI_PROVIDERS_CONFIG[pid].name}`, 'info');
  };

  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome-1',
      sender: 'assistant',
      text: "👋 Hi! I'm your **AzuraOS Concierge** — your live in-app navigator and founder support copilot.\n\nAsk me anything about setting up your workspace, configuring your free BYOK AI key, tracking real profit margins, reconciling bank statements via PDFs, CSVs, or copied lines, syncing Google Calendar, drafting client agreements, or exploring our **Pilot Cohort** program.\n\nWhere can I guide you today?",
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      quickAction: {
        label: 'Open AI Reconciler Hub',
        action: () => onNavigate('/ai-assistant?pillar=reconciler')
      }
    }
  ]);

  useEffect(() => {
    if (isWidgetOpen) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [messages, isWidgetOpen, keyboardOffset]);

  const handleSend = async (textToSend?: string) => {
    const query = (textToSend || inputMessage).trim();
    if (!query || isLoading) return;

    const userMsg: Message = {
      id: `u-${Date.now()}`,
      sender: 'user',
      text: query,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, userMsg]);
    setInputMessage('');
    setIsLoading(true);

    // 1. Check instant FAQ match first (0 tokens, zero latency!)
    const qLower = query.toLowerCase();
    const matchedFaq = INSTANT_FAQS.find(faq => 
      qLower.includes(faq.question.toLowerCase().slice(0, 15)) ||
      faq.question.toLowerCase().includes(qLower) ||
      (faq.keywords && faq.keywords.some(kw => qLower.includes(kw.toLowerCase())))
    );

    if (matchedFaq) {
      setTimeout(() => {
        const assistantMsg: Message = {
          id: `a-${Date.now()}`,
          sender: 'assistant',
          text: matchedFaq.answer,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          quickAction: matchedFaq.route ? {
            label: matchedFaq.label || `Open ${matchedFaq.route}`,
            action: () => {
              if (matchedFaq.route === '/what-is-azuraos' && onOpenWhatIsAzuraOS) {
                onOpenWhatIsAzuraOS();
              } else if (matchedFaq.route === '/security' && onOpenSecurity) {
                onOpenSecurity();
              } else if (matchedFaq.route === '/faq' && onOpenFAQ) {
                onOpenFAQ();
              } else if (matchedFaq.route === '/contact' && onOpenContact) {
                onOpenContact();
              } else {
                onNavigate(matchedFaq.route!);
              }
            }
          } : matchedFaq.onTrigger ? {
            label: matchedFaq.label || 'Contact Founder',
            action: matchedFaq.onTrigger
          } : matchedFaq.link ? {
            label: matchedFaq.label || 'Open Link',
            action: () => window.open(matchedFaq.link, '_blank')
          } : undefined
        };
        setMessages(prev => [...prev, assistantMsg]);
        setIsLoading(false);
      }, 250);
      return;
    }

    // 2. Otherwise query backend or client BYOK
    try {
      const personalKey = localStorage.getItem('azura_os_gemini_key');
      let replyText = '';

      if (personalKey && personalKey.trim().length >= 5) {
        try {
          // Direct client BYOK call with supported model and comprehensive in-app guidance
          const res = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-3.8-flash:generateContent?key=${personalKey}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              systemInstruction: {
                parts: [{
                  text: `You are the official AzuraOS Concierge — the dedicated, friendly, and knowledgeable in-app support specialist and platform navigator for solo founders and entrepreneurs.
Your role is to provide warm, conversational, step-by-step guidance, helping founders make the most of every feature inside AzuraOS.

CORE TONE & SUPPORT GUIDELINES:
- Warm, empathetic, encouraging, and highly actionable (like a top-tier startup support lead or executive concierge desk).
- Never give cold, generic robotic corporate replies. Talk directly and warmly to the founder.
- Clearly explain concepts simply in plain English.
- Always provide EXACT IN-APP NAVIGATION paths (e.g. "Tap the Menu (top right) ➔ ...", "Go to /dashboard ➔ ...", or "Head to the Invoices tab").
- If they ask about the Pilot Cohort program:
  Explain it warmly and conversationally: The Pilot Cohort is AzuraOS's turnkey 30-day financial governance and solvency initiative built for batches of 10 to 100+ solo founders and SMEs sponsored by incubators, accelerators, venture studios, or commercial banks. Instead of discovering cash crises months late through retroactive accounting, AzuraOS provides real-time solvency telemetry from Day 1. Guide them to:
  * Interactive Cohort ROI Calculator & Playbook: Tap Menu (top right) ➔ "What is AzuraOS?" or "Concept & Mindset".
  * Enterprise & Bank Sponsoring inquiries: Tap Menu ➔ under "ENTERPRISE & BANKS", select "Partnership Access" or email support@azuraos.app.

KEY IN-APP NAVIGATION & MODULES:
- /ai-assistant:
  * "AI Reconciler" tab: Automated statement categorization, multi-stream attribution, tax-deductible expense matching, and receipt/invoice line-item OCR.
  * "AI Co-Founder" tab: Strategic advisory, unit economics, pitch deck reviews, and business expansion.
  * "Client Agreements & Proforma" tab: Commercial contracts, milestone proformas, and term sheets in Sovereign BYOK Mode.
- /dashboard: Margin Radar, live revenue tracking, expense breakdown, and health alerts.
- /bookings: Client pipeline, confirmed/pending payments, delivery dates.
- /expenses: Categorized business expenditures and tax deductions.
- /invoices: Instant proforma and tax invoices, PDF exports.
- /calendar: 2-way Google Calendar synchronization.
- /j-lens: Cash flow runway simulator and scenario stress-testing.
- /welcome: Onboarding hub & 60-second BYOK Google AI Studio key setup.
- /business-hq & /settings: Business profile, currency, tax presets (SARS, IRS, VAT, GST), and local backups.
- /billing: 7-day trial status, subscription tiers, and payment sync.

AI RECONCILER & MULTI-FEATURE INGESTION KNOWLEDGE:
1. Multi-Modal Statement Ingestion (PDF, CSV, Paste, Manual):
   - Full support for direct PDF bank statements (with built-in stream decompression and privacy-first extraction for FNB, Chase, Capitec, Barclays, Standard Bank, etc.).
   - Ultra-fast 0.05s CSV/Excel bank export parsing.
   - 1-click 'Paste Banking Lines' modal for copying rows straight from online banking portals or PDF readers.
   - Inline manual entry grid with automatic stream mapping and recurring SaaS detection.
2. Receipts & Invoices:
   - Snap phone photos / screenshots (.jpg, .png) with auto-compression.
   - Press Ctrl+V anywhere on screen to paste images directly from clipboard.
   - Click "Fill Manually" to log an expense draft without uploading an image.

IN-APP UTILITIES & GUIDANCE:
- Privacy Mode: Tap the Menu -> Privacy Mode (Masked/Visible) to hide all numbers when in public.
- Multi-Language: Select among 15+ languages at the bottom of the Menu drawer.
- Zero Model Training: All numbers are private. No AI model training on business data.
- Support email: support@azuraos.app.

TRUST, CERTIFICATIONS & REGULATORY GUIDELINES:
If asked about security certificates, trust, banking licenses, CPA credentials, or regulative permissions:
- Explain that AzuraOS is non-custodial operational intelligence.
- Inherited Tier-1 Google Cloud & Firebase Firestore infrastructure certifications: SOC 1, SOC 2 Type II, SOC 3, ISO/IEC 27001, 27017, 27018, and PCI-DSS Level 1.
- Member of Google Cloud Partner Advantage Network & Google for Startups Cloud Program (Start Tier).
- No Banking / AISP License needed because AzuraOS never scrapes banking passwords or connects to live bank feeds directly. Founders upload CSV statements or paste rows.
- No Money Transmitter License needed because AzuraOS never holds or moves money. Subscriptions are processed through our Merchant of Record (Dodo Payments) with PCI-DSS Level 1 tokenization.
- AI CFO / Legal Co-Founder outputs are operational decision math and strategy copilots, not regulated CPA fiduciary accounting or Bar-licensed legal advice.
- Browser-native AES-256-GCM Web Crypto vault with Zero-Knowledge encryption for sensitive records and secrets.
- Direct them to: "Tap 'Trust & Security' in the top header or Menu to inspect the live vault and certifications matrix."`
                }]
              },
              contents: [{ role: 'user', parts: [{ text: query }] }]
            })
          });
          if (res.ok) {
            const data = await res.json().catch(() => null);
            replyText = data?.candidates?.[0]?.content?.parts?.[0]?.text || '';
          }
        } catch (byokErr) {
          console.warn('BYOK direct call fell back to server concierge:', byokErr);
        }
      }

      // Fallback to server endpoint if direct client call didn't produce a reply
      if (!replyText) {
        try {
          const byokHeaders = getByokRequestHeaders();
          const res = await fetch('/api/ai-concierge', {
            method: 'POST',
            headers: byokHeaders,
            body: JSON.stringify({
              message: query,
              conversationHistory: messages.slice(-6)
            })
          });
          const contentType = res.headers.get('content-type') || '';
          const data = contentType.includes('application/json') ? await res.json().catch(() => ({})) : {};
          replyText = data.reply || data.fallbackReply || 'I am ready to help you with AzuraOS!';
        } catch (serverErr) {
          console.warn('Concierge fallback error:', serverErr);
          replyText = 'I am ready to help you with AzuraOS! You can navigate to /dashboard, /invoices, /expenses, or connect your Google AI Studio key in /welcome.';
        }
      }

      const assistantMsg: Message = {
        id: `a-${Date.now()}`,
        sender: 'assistant',
        text: replyText || "I'm here to assist! Ask me any question about your dashboard, invoices, bookings, or settings.",
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };

      setMessages(prev => [...prev, assistantMsg]);
    } catch (err) {
      console.error('Concierge request error:', err);
      const fallbackMsg: Message = {
        id: `a-${Date.now()}`,
        sender: 'assistant',
        text: "I am ready to help you navigate AzuraOS. You can ask about BYOK setup, margin calculations, bookings, invoices, or calendar sync.",
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        quickAction: {
          label: 'Open Welcome & Quickstart Hub',
          action: () => onNavigate('/welcome')
        }
      };
      setMessages(prev => [...prev, fallbackMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearHistory = () => {
    setMessages([
      {
        id: 'welcome-reset',
        sender: 'assistant',
        text: 'Chat history cleared. How can I help you with AzuraOS today?',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }
    ]);
  };

  // If widget is closed, render the floating trigger bubble (compact on mobile, clear branding)
  if (!isWidgetOpen) {
    return (
      <div className="fixed bottom-4 right-4 sm:bottom-5 sm:right-5 z-40">
        <button
          id="ai-concierge-trigger"
          type="button"
          onClick={toggleWidget}
          className="group relative flex items-center gap-1.5 sm:gap-2.5 p-2.5 sm:px-4 sm:py-3 rounded-full bg-gradient-to-r from-[#14B8A6] via-[#0EA5A4] to-[#0D9488] text-black font-bold shadow-xl shadow-[#14B8A6]/20 hover:shadow-[#14B8A6]/35 hover:scale-105 active:scale-95 transition-all cursor-pointer border border-white/20 select-none"
          aria-label="Open AzuraOS AI Concierge Help"
          title="AzuraOS AI Concierge & In-App Copilot"
        >
          {/* Animated Avatar Icon */}
          <div className="relative flex items-center justify-center shrink-0">
            <div className="w-7 h-7 sm:w-6 sm:h-6 rounded-full bg-black/15 flex items-center justify-center">
              <Bot size={16} className="text-black stroke-[2.2px]" />
            </div>
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-white animate-ping opacity-75" />
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-white shadow-sm ring-1 ring-[#0D9488]" />
          </div>

          {/* Desktop Text */}
          <span className="font-display tracking-tight text-xs font-extrabold hidden sm:inline">
            AI Concierge
          </span>

          {/* Mobile Badge - Clear that it's AI Help / Concierge, not WhatsApp */}
          <span className="text-[10px] font-extrabold uppercase font-mono tracking-tight sm:hidden pr-1 text-black">
            AI Help
          </span>

          {/* Desktop Sub-badge */}
          <span className="hidden sm:inline-block text-[9px] px-2 py-0.5 rounded-full bg-black/15 uppercase font-mono font-bold tracking-tight">
            In-App Copilot
          </span>
        </button>
      </div>
    );
  }

  // Dynamic style for mobile virtual keyboard avoidance
  const dynamicPanelStyle: React.CSSProperties = keyboardOffset > 0 && viewportHeight ? {
    bottom: `${keyboardOffset + 8}px`,
    maxHeight: `${Math.max(280, viewportHeight - 16)}px`,
    height: `${Math.max(280, viewportHeight - 16)}px`
  } : {};

  return (
    <div 
      className="fixed inset-x-2 bottom-3 sm:inset-auto sm:bottom-5 sm:right-5 sm:w-[400px] h-[540px] max-h-[85dvh] sm:max-h-[80vh] rounded-3xl bg-[#121617] border border-[#20272A] shadow-2xl flex flex-col overflow-hidden z-50 animate-fade-in transition-all"
      style={dynamicPanelStyle}
    >
      {/* Header */}
      <div className="px-4 py-3 bg-[#161B1D] border-b border-[#20272A] flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-[#14B8A6]/15 text-[#14B8A6] flex items-center justify-center relative">
            <Bot size={18} />
            <span className="w-2 h-2 rounded-full bg-emerald-400 absolute -top-0.5 -right-0.5 ring-2 ring-[#161B1D]" />
          </div>
          <div>
            <div className="text-xs font-semibold text-white flex items-center gap-1.5">
              <span>AzuraOS Concierge</span>
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#14B8A6]/20 text-[#14B8A6] font-mono">
                IN-APP SUPPORT &amp; GUIDE
              </span>
            </div>
            <div className="text-[10px] text-gray-400 flex items-center gap-1">
              <ShieldCheck size={11} className="text-emerald-400" />
              <span>Live Founder Support • Zero Surveillance</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={handleClearHistory}
            className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-[#20272A] transition-all cursor-pointer"
            title="Clear history"
          >
            <RotateCcw size={14} />
          </button>
          <button
            onClick={toggleWidget}
            className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-[#20272A] transition-all cursor-pointer"
            title="Close Concierge"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* AI Provider Engine Selection Bar (Gemini, OpenAI, Anthropic, etc.) */}
      <div className="px-3.5 py-2 bg-[#0B0E0F] border-b border-[#1E2528] flex items-center justify-between gap-2 shrink-0">
        <div className="flex items-center gap-1.5 text-[11px] text-gray-400 font-medium">
          <Key size={12} className="text-[#14B8A6]" />
          <span>Engine:</span>
        </div>

        <div className="flex items-center gap-1.5 flex-1 justify-end">
          <select
            value={activeProvider}
            onChange={(e) => handleProviderSelect(e.target.value as AiProviderId)}
            className="bg-[#161C1E] border border-[#273337] text-white text-[11px] font-semibold rounded-lg px-2.5 py-1 focus:outline-none focus:border-[#14B8A6] cursor-pointer transition-colors"
          >
            <option value="gemini">Google Gemini (1,500 free/day)</option>
            <option value="openai">OpenAI (GPT-4o &amp; o3-mini)</option>
            <option value="anthropic">Anthropic (Claude 3.7 Sonnet)</option>
            <option value="groq">Groq Cloud (500 tokens/sec)</option>
            <option value="deepseek">DeepSeek (V3 &amp; R1)</option>
            <option value="openrouter">OpenRouter (Multi-Model)</option>
            <option value="mistral">Mistral AI (Large &amp; Codestral)</option>
            <option value="perplexity">Perplexity AI (Live Sonar)</option>
          </select>

          <button
            type="button"
            onClick={() => onNavigate('/welcome')}
            className="p-1 rounded-md text-gray-400 hover:text-white hover:bg-[#1E2528] transition-colors"
            title="Manage BYOK Keys in Settings / Welcome"
          >
            <ExternalLink size={12} />
          </button>
        </div>
      </div>

      {/* Quick FAQ Chips Carousel */}
      <div className="px-3 py-2 bg-[#0E1214] border-b border-[#1A2022] flex items-center gap-1.5 overflow-x-auto no-scrollbar shrink-0">
        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider shrink-0 flex items-center gap-1 pl-1">
          <Sparkles size={11} className="text-[#14B8A6]" /> FAQs:
        </span>
        {INSTANT_FAQS.map((faq, i) => (
          <button
            key={i}
            onClick={() => handleSend(faq.question)}
            className="text-[11px] px-2.5 py-1 rounded-full bg-[#181E21] hover:bg-[#14B8A6]/20 hover:text-[#14B8A6] text-gray-300 border border-[#242D30] transition-all whitespace-nowrap shrink-0 cursor-pointer"
          >
            {faq.question}
          </button>
        ))}
      </div>

      {/* Messages Body */}
      <div className="flex-1 p-4 overflow-y-auto space-y-3 text-xs bg-[#101415]">
        {messages.map((m) => {
          const isUser = m.sender === 'user';
          return (
            <div
              key={m.id}
              className={`flex gap-2.5 ${isUser ? 'justify-end' : 'justify-start'}`}
            >
              {!isUser && (
                <div className="w-6 h-6 rounded-lg bg-[#14B8A6]/20 text-[#14B8A6] flex items-center justify-center shrink-0 mt-0.5">
                  <Bot size={13} />
                </div>
              )}

              <div className={`space-y-1.5 max-w-[85%] ${isUser ? 'items-end' : 'items-start'}`}>
                <div
                  className={`p-3.5 rounded-2xl leading-relaxed ${
                    isUser
                      ? 'bg-[#14B8A6] text-black font-semibold rounded-tr-none text-xs whitespace-pre-wrap'
                      : 'bg-[#181E21] text-gray-200 border border-[#242D30] rounded-tl-none shadow-sm'
                  }`}
                >
                  {isUser ? (
                    m.text
                  ) : (
                    <FormattedMarkdown content={m.text} />
                  )}
                </div>

                {m.quickAction && (
                  <button
                    onClick={m.quickAction.action}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#14B8A6]/15 hover:bg-[#14B8A6]/25 border border-[#14B8A6]/30 text-[#14B8A6] text-[11px] font-semibold transition-all cursor-pointer"
                  >
                    <span>{m.quickAction.label}</span>
                    <ArrowRight size={12} />
                  </button>
                )}

                <div className={`text-[9px] text-gray-400 px-1 ${isUser ? 'text-right' : 'text-left'}`}>
                  {m.timestamp}
                </div>
              </div>

              {isUser && (
                <div className="w-6 h-6 rounded-lg bg-gray-700 text-gray-300 flex items-center justify-center shrink-0 mt-0.5">
                  <User size={13} />
                </div>
              )}
            </div>
          );
        })}

        {isLoading && (
          <div className="flex items-center gap-2 p-3 rounded-2xl bg-[#181E21] border border-[#242D30] text-gray-400 text-xs w-fit">
            <Loader2 size={13} className="animate-spin text-[#14B8A6]" />
            <span>AzuraOS Copilot is typing...</span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Box */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSend();
        }}
        className="p-2.5 bg-[#161B1D] border-t border-[#20272A] flex items-center gap-2 shrink-0"
      >
        <input
          ref={inputRef}
          type="text"
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onFocus={() => {
            setTimeout(() => {
              messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
              inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }, 200);
          }}
          placeholder="Ask about AI Reconciler, CSV uploads, margins, invoices..."
          className="flex-1 px-3.5 py-2.5 rounded-xl bg-[#101415] border border-[#262E32] text-xs text-white placeholder-gray-500 focus:outline-none focus:border-[#14B8A6] focus:ring-1 focus:ring-[#14B8A6]/50 transition"
        />
        <button
          type="submit"
          disabled={!inputMessage.trim() || isLoading}
          className="p-2.5 rounded-xl bg-[#14B8A6] hover:bg-[#0EA5A4] disabled:opacity-40 text-black font-bold transition-all cursor-pointer shrink-0"
          title="Send message"
        >
          <Send size={15} />
        </button>
      </form>
    </div>
  );
}
